/**
 * Ad-continuity variants for the /explore landing page.
 *
 * Each variant mirrors the copy of the ad that sent the visitor, keeping
 * scent between the ad clicked and the page seen. Unknown/absent signals
 * fall back to the generic variant, which is independent of any ad.
 *
 * Two ad platforms send two different signals, so the resolver reads both
 * (see `resolveExploreVariant`):
 *
 * - **Google Search** (`g-search-web-selfadhd-us`) appends
 *   `utm_term={keyword}` via the campaign's final_url_suffix, so the matched
 *   search keyword itself lands on the URL.
 * - **Meta** (`Q3'26 [V2-W] Web /explore — US`) appends a fixed parameter
 *   block built from its own macros:
 *   `utm_id={{campaign.id}}`, `utm_source=facebook`, `utm_medium=paid`,
 *   `utm_campaign=v2-web-explore`, `utm_term={{adset.id}}`,
 *   `utm_content={{ad.name}}` (slugified, e.g. `w2-no-streaks`).
 *   Note Meta's `utm_term` is the **ad set id**, a bare number — it carries
 *   no keyword, which is why matching on `utm_term` alone sent 100% of Meta
 *   traffic to the generic variant. `utm_content` is the per-ad signal.
 *
 * **Adding a new ad requires no code change.** Name the ad so its slug
 * contains the intent word the matchers already look for — "W5 Gentle pet
 * companion" slugifies to `w5-gentle-pet-companion` and matches `companion`.
 * When a name can't carry the word, append `&variant=<id>` to that ad's URL
 * parameters; an explicit `variant` always wins.
 */

export type ExploreVariantId =
  | "task"
  | "routines"
  | "habits"
  | "meds"
  | "companion"
  | "dental"
  | "generic";

export interface ExploreVariant {
  id: ExploreVariantId;
  /** Short label for the test-mode variant picker. */
  label: string;
  /** Emberlee's speech-bubble message. */
  message: string;
  /** Markdown body shown in the action card. */
  body: string;
  /** CTA button text. */
  cta: string;
  /** Reassurance line under the CTA (ad variants only). */
  microcopy?: string;
}

// Honest framing: starting is account-free, but saving the character requires signup 
// Rachel removed "free to try" language on 9-3-2026 with dental feature deployment since paywalls have been disabled for now.
const AD_CTA = "Let's go! 🚀";
const AD_MICROCOPY = "Start right in your browser · about 2 minutes";

export const EXPLORE_VARIANTS: ExploreVariant[] = [
  {
    id: "task",
    label: "Task initiation",
    message:
      "Hi! I'm Emberlee. That task you've been avoiding? Let's make it doable — together.",
    body: `You'll create a companion **made for ADHD brains** who:

- 🧩 Breaks big tasks into tiny, doable steps
- 🎉 Cheers you through each one
- 💛 Helps you *start* — no shame, no pressure

Other apps hand you a list. Your companion helps you begin.`,
    cta: AD_CTA,
    microcopy: AD_MICROCOPY,
  },
  {
    id: "routines",
    label: "Routines & reminders",
    message:
      "Hi! I'm Emberlee. Let's build a daily routine your ADHD brain will actually enjoy.",
    body: `You'll create a companion **made for ADHD brains** who:

- 🗓️ Guides your day with friendly reminders
- 🧩 Makes routines doable, step by step
- 💛 Checks in like a friend — never a blaring alarm

Whimsical, not clinical. Built for unique minds.`,
    cta: AD_CTA,
    microcopy: AD_MICROCOPY,
  },
  {
    id: "habits",
    label: "Gentle habits",
    message:
      "Hi! I'm Emberlee. No streaks. No guilt. Just a friend who's glad you're here.",
    body: `You'll create a companion **made for ADHD brains** who:

- 💛 Cheers, never nags
- 🌱 Says "welcome back" when you miss a day — that's it
- 🎉 Celebrates every small win

Soft and whimsical, never a blaring alarm.`,
    cta: AD_CTA,
    microcopy: AD_MICROCOPY,
  },
  {
    id: "meds",
    label: "Medication reminders",
    message:
      "Hi! I'm Emberlee. Gentle medication reminders — never a nag, always a friend.",
    body: `You'll create a companion **made for ADHD brains** who:

- 💊 Reminds you warmly: "Evening pill time — I'll sit with you while you take it."
- 💛 Never guilts you when life happens
- 🗓️ Can help with routines and check-ins too

Your reminder, your character.`,
    cta: AD_CTA,
    microcopy: AD_MICROCOPY,
  },
  {
    id: "companion",
    label: "Companion / pet",
    message:
      "Hi! I'm Emberlee. Let's turn what you love into your own cheer squad.",
    body: `Snap a photo of your pet or plushie — and create a companion **built for ADHD brains** who:

- 📸 Comes to life from your photo
- 💛 Cheers on your day — a companion, not a checklist
- 🎉 Makes routines and tasks feel joyful

More than a self-care pet.`,
    cta: AD_CTA,
    microcopy: AD_MICROCOPY,
  },
  {
    // The dental pilot's entry point. Landing here is what enrols someone —
    // see ExplorePage.handleStart, which stamps `dentalPilot` on the account
    // when this variant is the one that rendered. No dental ads exist yet;
    // this is the hook for them. Until an ad's name carries a dental word,
    // pin it explicitly with `&variant=dental` — that always wins.
    //
    // Copy mirrors DENTAL_WELCOME_LABEL in services/backend/.../onboarding.ts.
    // Ad traffic never sees that step (the CTA resumes onboarding past it), so
    // this page has to carry the dental welcome itself.
    id: "dental",
    label: "Dental pilot",
    message:
      "Welcome to Unique Minds! I'm Emberlee, your guide! We're going to create something amazing together.",
    body: `You'll create a **Support Crew** of characters for you or someone you care about — and watch them come to life to help you:

- 🪥 Build healthy habits
- 🦷 Feel more prepared for dental visits
- 🌟 Make everyday routines easier
- 💖 Feel more confident & capable

They're here to help make your smile shine bright – and the rest of life shine brighter. ✨`,
    cta: AD_CTA,
    microcopy: AD_MICROCOPY,
  },
  {
    id: "generic",
    label: "Generic (fallback)",
    message:
      "Welcome to Unique Minds! I'm Emberlee, your guide! We're going to create something amazing together.",
    body: `You'll create a **Support Crew** of characters for you or someone you care about — and watch them come to life to help with things like:

- 🗓️ Daily routines & reminders
- 🌟 Fun activities and stories
- 💖 Encouraging check-ins

They're here to make life easier — and a little more magical. ✨`,
    cta: "Let's Go! 🚀",
  },
];

/**
 * Ordered keyword → variant matchers. Order matters: more specific intents
 * are checked before broader ones (e.g. "medication reminder app adhd" must
 * hit meds before the generic-adhd routines matcher; "executive dysfunction"
 * is a task-initiation keyword while "executive function" belongs to
 * routines). Brand terms fall through to generic — brand searchers already
 * know the product, so ad-specific framing adds nothing.
 */
const VARIANT_MATCHERS: Array<{ id: ExploreVariantId; pattern: RegExp }> = [
  { id: "generic", pattern: /unique ?minds/ },
  // Ahead of every other intent: "dental reminder" is dental before it is a
  // reminder, and "healthy smile habits" is dental before it is a habit.
  //
  // This matcher is not just copy scent — for the other variants a misroute
  // changes the landing copy, for this one it decides whether the account is
  // enrolled at all, and there is no way to self-enrol afterwards. So it is
  // deliberately generous on the words a dental campaign will actually buy:
  // "brushing routine", "toothbrush timer", "oral hygiene" all count.
  // `\btooth` is prefix-bounded on purpose (toothbrush, toothpaste — but not
  // bluetooth); `\bteeth\b` is word-bounded so "teething" stays a parenting
  // search; `\bbrush` keeps "hairbrush" out.
  {
    id: "dental",
    pattern:
      /dental|dentist|orthodont|braces|\bteeth\b|\btooth|\bbrush(?:ing|es|ed)?\b|floss|mouthwash|\bsmile\b|oral ?(?:care|hygiene|health)|invisalign|retainer/,
  },
  { id: "meds", pattern: /\bmed(s|ication|icine)?\b|\bpill\b/ },
  { id: "habits", pattern: /habit|streak|guilt/ },
  {
    id: "companion",
    pattern: /companion|\bpet\b|plush|body doubling|finch|tiimo|routinery|self care/,
  },
  {
    id: "task",
    pattern: /task|paralysis|executive dysfunction|goblin|magic todo|\bstart\b|focus/,
  },
  {
    id: "routines",
    pattern: /adhd|routine|reminder|planner|to.?do|productivity|executive function/,
  },
];

export function matchExploreVariant(
  utmTerm: string | null | undefined
): ExploreVariantId {
  if (!utmTerm) return "generic";
  const term = utmTerm.toLowerCase();
  for (const { id, pattern } of VARIANT_MATCHERS) {
    if (pattern.test(term)) return id;
  }
  return "generic";
}

const GENERIC_VARIANT = EXPLORE_VARIANTS.find((v) => v.id === "generic")!;

export function getExploreVariant(id: ExploreVariantId): ExploreVariant {
  // By id, not index: the fallback was `EXPLORE_VARIANTS[5]`, which was
  // generic only while generic happened to be sixth — adding a variant above
  // it would have silently made that variant the fallback for everyone.
  return EXPLORE_VARIANTS.find((v) => v.id === id) ?? GENERIC_VARIANT;
}

/** Query param that pins a variant outright, e.g. `?variant=habits`. */
export const EXPLORE_VARIANT_QUERY_PARAM = "variant";

/** Which signal decided the variant — reported to analytics. */
export type ExploreVariantSource =
  | "param" // explicit ?variant=
  | "ad" // utm_content — the ad's slugified name (Meta)
  | "keyword" // utm_term — the matched search keyword (Google)
  | "default"; // nothing usable on the URL

export interface ResolvedExploreVariant {
  id: ExploreVariantId;
  source: ExploreVariantSource;
  /** The raw value the decision came from, for analytics. */
  signal?: string;
}

function asVariantId(
  value: string | null | undefined
): ExploreVariantId | undefined {
  if (!value) return undefined;
  const id = value.trim().toLowerCase();
  return EXPLORE_VARIANTS.some((v) => v.id === id)
    ? (id as ExploreVariantId)
    : undefined;
}

/**
 * Meta fills several utm_* values with bare numeric ids (`utm_term` is the ad
 * set id, `utm_id` the campaign id). Those can never carry intent, so skip any
 * value without a letter in it rather than running it through the matchers.
 */
function carriesWords(value: string | null | undefined): value is string {
  return !!value && /[a-z]/i.test(value);
}

/**
 * Resolve which variant to present, in priority order:
 *
 *  1. `?variant=<id>` — an explicit pin, for ads whose name can't carry the
 *     intent word. Always wins.
 *  2. `utm_content` — Meta's slugified ad name (`w2-no-streaks`). Descriptive
 *     ad names route themselves, so new ads need no code change.
 *  3. `utm_term` — Google's matched search keyword.
 *  4. generic.
 *
 * Steps 2 and 3 run the value through the same keyword matchers; a `generic`
 * result is treated as "no signal here" and resolution continues, so a Meta ad
 * whose name says nothing still gets a chance to match on a Google keyword.
 */
export function resolveExploreVariant(
  search: string | URLSearchParams
): ResolvedExploreVariant {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;

  const pinned = asVariantId(params.get(EXPLORE_VARIANT_QUERY_PARAM));
  if (pinned) {
    return { id: pinned, source: "param", signal: pinned };
  }

  const adSlug = params.get("utm_content");
  if (carriesWords(adSlug)) {
    const id = matchExploreVariant(adSlug);
    if (id !== "generic") return { id, source: "ad", signal: adSlug };
  }

  const keyword = params.get("utm_term");
  if (carriesWords(keyword)) {
    const id = matchExploreVariant(keyword);
    if (id !== "generic") return { id, source: "keyword", signal: keyword };
  }

  return {
    id: "generic",
    source: "default",
    signal: adSlug ?? keyword ?? undefined,
  };
}

/**
 * Slugify an ad name the way Meta's `{{ad.name}}` macro does, so a name
 * pasted straight out of Ads Manager ("W2 No streaks") can be tested as the
 * `utm_content` value it will actually arrive as (`w2-no-streaks`).
 */
export function slugifyAdName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export interface ExploreVariantProbe extends ResolvedExploreVariant {
  /** The query string the input was interpreted as. */
  query: string;
  /** Plain-language explanation of how the input was read. */
  note: string;
}

/**
 * Test-mode helper: resolve a free-text probe exactly the way a real visit
 * would. Accepts any of
 *
 *  - a search term — `habit tracker without streaks`
 *  - an ad name or slug — `W2 No streaks` / `w2-no-streaks`
 *  - a pasted URL or query string — `...explore?utm_content=w2-no-streaks`
 *  - a bare id — `52576648418570` (demonstrates the fallback)
 */
export function probeExploreVariant(input: string): ExploreVariantProbe {
  const raw = input.trim();
  if (!raw) {
    return {
      id: "generic",
      source: "default",
      query: "",
      note: "Type a search term, an ad name, or paste a landing URL.",
    };
  }

  // A pasted URL or query string — resolve it verbatim, params and all.
  if (raw.includes("=")) {
    const q = raw.indexOf("?");
    const query = q >= 0 ? raw.slice(q + 1) : raw;
    const resolved = resolveExploreVariant(query);
    return {
      ...resolved,
      query,
      note:
        resolved.source === "default"
          ? "No usable signal in these parameters — a real visit shows the generic page."
          : `Resolved from ${resolved.source === "param" ? "an explicit ?variant" : resolved.source === "ad" ? "utm_content (ad name)" : "utm_term (search keyword)"}.`,
    };
  }

  // Bare text: could be an ad name (Meta) or a search term (Google). Both run
  // through the same matchers, so report each reading — they only disagree
  // when slugification changes the words.
  const slug = slugifyAdName(raw);
  const asAd = matchExploreVariant(slug);
  const asKeyword = matchExploreVariant(raw);
  const query = `utm_content=${encodeURIComponent(slug)}&utm_term=${encodeURIComponent(raw)}`;

  if (!carriesWords(raw)) {
    return {
      id: "generic",
      source: "default",
      signal: raw,
      query,
      note: "No letters. Meta puts bare ids in utm_term/utm_id, so values like this are ignored and the page falls back to generic.",
    };
  }

  if (asAd !== "generic") {
    return {
      id: asAd,
      source: "ad",
      signal: slug,
      query,
      note:
        asKeyword === asAd
          ? `Matches as an ad name (${slug}) and as a search term.`
          : `Matches as an ad name (${slug}).`,
    };
  }

  if (asKeyword !== "generic") {
    return {
      id: asKeyword,
      source: "keyword",
      signal: raw,
      query,
      note: `Matches as a search term, but not once slugified to "${slug}" — a Meta ad with this name would fall back to generic.`,
    };
  }

  return {
    id: "generic",
    source: "default",
    signal: raw,
    query,
    note: "No matcher hits this. A real visit would show the generic page — add a keyword to the ad name, or pin it with &variant=<id>.",
  };
}
